/** Per-driver spread of a count over a computed Result's routes. */
export type PerDriverSummary = {
  min: number | null;
  median: number | null;
  max: number | null;
  average: number | null;
};

/** One per-driver histogram bucket, e.g. 10..19 -> 3 drivers. */
export type PerDriverBucket = {
  from: number;
  to: number;
  drivers: number;
};

const NOT_COUNTED = '—';

/**
 * A per-driver ratio as the API reports it on a depot summary: rounded to one
 * decimal, and zero for a depot that needs no driver. Used wherever a row is
 * added up from the summary fields rather than read off one.
 */
export const perDriverOf = (count: number, drivers: number): number =>
  drivers === 0 ? 0 : Math.round((count / drivers) * 10) / 10;

const median = (sortedValues: number[]) => {
  const mid = Math.floor(sortedValues.length / 2);
  return sortedValues.length % 2 === 0
    ? (sortedValues[mid - 1] + sortedValues[mid]) / 2
    : sortedValues[mid];
};

/**
 * min / median / max / average of `values`, or all nulls for none. The average
 * is rounded to one decimal like the other per-driver ratios.
 */
export function summarizeCounts(values: number[]): PerDriverSummary {
  if (values.length === 0) {
    return { min: null, median: null, max: null, average: null };
  }

  const sorted = [...values].sort((a, b) => a - b);
  const sum = sorted.reduce((total, value) => total + value, 0);

  return {
    min: sorted[0],
    max: sorted[sorted.length - 1],
    median: median(sorted),
    average: perDriverOf(sum, sorted.length),
  };
}

/**
 * Ordered histogram buckets of `bucketSize` values, covering min..max with no
 * empty leading or trailing bucket. Empty for no values.
 */
export function bucketCounts(
  values: number[],
  bucketSize = 10,
): PerDriverBucket[] {
  if (values.length === 0) {
    return [];
  }

  const bucketStart = (value: number) =>
    Math.floor(value / bucketSize) * bucketSize;
  const firstFrom = bucketStart(Math.min(...values));
  const lastFrom = bucketStart(Math.max(...values));

  const buckets: PerDriverBucket[] = [];
  for (let from = firstFrom; from <= lastFrom; from += bucketSize) {
    const to = from + bucketSize - 1;
    buckets.push({
      from,
      to,
      drivers: values.filter((value) => value >= from && value <= to).length,
    });
  }

  return buckets;
}

/**
 * Runs computed before recipients were counted carry 0 for them; a dash says
 * "not known" where 0 would wrongly say "nobody".
 */
export const recipientsText = (
  recipients: number,
  deliveries: number,
): string =>
  recipients === 0 && deliveries > 0
    ? NOT_COUNTED
    : recipients.toLocaleString();

/**
 * Same "not known" rule for the ratio: with drivers but no routed recipients
 * the run predates the count, so show a dash rather than 0.0.
 */
export const recipientsPerDriverText = ({
  routedRecipients,
  recipientsPerDriver,
  drivers,
}: {
  routedRecipients: number;
  recipientsPerDriver: number;
  drivers: number;
}): string =>
  routedRecipients === 0 && drivers > 0
    ? NOT_COUNTED
    : recipientsPerDriver.toFixed(1);
